import React, {useCallback, useEffect, useMemo, useState} from "react";
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Link,
    Paper,
    Stack,
    Tab,
    Tabs,
    Typography
} from "@mui/material";
import {Link as RouterLink, Navigate, useNavigate, useParams} from "react-router-dom";
import {FeedbackSnackbar} from "../../shared/ui";
import {getBackendErrorMessage} from "../../shared/utils/httpError";
import {formatDataIsoBR} from "../../shared/utils/formatters";
import {SistemaLogo} from "../Sistemas/components/SistemaVisual";
import PlanosLayout from "./components/PlanosLayout";
import ComercialCard from "./components/ComercialCard";
import AcessoCard from "./components/AcessoCard";
import AssinaturasDaVersao from "./components/AssinaturasDaVersao";
import SimuladorCard from "./components/SimuladorCard";
import PerfisDoPlanoCard from "./components/PerfisDoPlanoCard";
import {createRascunho, deleteRascunho, publicarVersao, updatePlano, updateRascunho} from "./PlanosComerciaisService";
import {usePlanoVersao} from "./usePlanoVersao";
import {
    alternarMeio,
    assinaturasPorVersao,
    avisosDoRascunho,
    corpoDoPlano,
    corpoDoRascunho,
    definirPrecoDoOpcional,
    definirSituacaoDoModulo,
    efeitoDePublicar,
    efeitoNaAnterior,
    formDaVersao,
    getSituacaoVersao,
    plural,
    proximoNumero,
    rascunhoAlterado,
    rascunhoDe,
    versaoBase,
    versaoPrincipal
} from "./planoVersaoUtils";

const valorDe = (response) => response?.data?.valor ?? null;

// O que a pessoa precisa saber da versão antes de mexer (protótipo v2, `statusCallout`).
function SituacaoDaVersao({versao, versoes}) {
    const aVenda = versoes.find((item) => item.situacao === 'A_VENDA');
    if (versao.situacao === 'RASCUNHO') {
        return (
            <Alert severity="info">
                <b>Rascunho.</b> Ninguém assina esta versão. Ajuste e publique quando estiver pronta.
                {aVenda ? ` Ao publicar: ${efeitoNaAnterior(versao, versoes).replace(/^A/, 'a')}` : ''}
            </Alert>
        );
    }
    if (versao.situacao === 'A_VENDA') {
        return <Alert severity="success" icon={false}><b>À venda{versao.publicada_em ? ` desde ${formatDataIsoBR(versao.publicada_em)}` : ''}.</b> Versão publicada não muda. Para alterar preço, meios ou módulos, crie uma versão nova a partir desta.</Alert>;
    }
    if (versao.situacao === 'EM_USO') {
        return <Alert severity="warning" icon={false}><b>Fora de venda.</b> Segue valendo para {plural(versao.assinaturas || 0, 'assinatura', 'assinaturas')} que já estão nesta versão. Nada aqui muda; para levar uma delas à versão à venda, use a troca de plano na assinatura.</Alert>;
    }
    return <Alert severity="info" icon={false}><b>Encerrada.</b> Fora de venda e sem assinaturas. Fica como histórico.</Alert>;
}

// Pendências que a API calcula para o rascunho: os erros impedem publicar; os avisos, não.
function PendenciasDoRascunho({pendencias, avisosLocais, sujo}) {
    const erros = pendencias?.erros || [];
    const avisos = pendencias?.avisos || [];
    if (!erros.length && !avisos.length && !(sujo && avisosLocais.length)) return null;
    return (
        <Stack spacing={1}>
            {erros.length ? (
                <Alert severity="warning">
                    <b>{plural(erros.length, 'pendência impede', 'pendências impedem')} publicar.</b> O rascunho continua salvo.
                    <Box component="ul" sx={{m: 0, pl: 2.5}}>{erros.map((erro) => <li key={erro}>{erro}</li>)}</Box>
                </Alert>
            ) : null}
            {avisos.length ? (
                <Alert severity="info">
                    <Box component="ul" sx={{m: 0, pl: 2.5}}>{avisos.map((aviso) => <li key={aviso}>{aviso}</li>)}</Box>
                </Alert>
            ) : null}
            {sujo && avisosLocais.length ? (
                <Alert severity="info">Ainda não salvo: {avisosLocais.join(' ')}</Alert>
            ) : null}
        </Stack>
    );
}

function Confirmacao({aberto, titulo, texto, rotulo, perigo, ocupado, onCancelar, onConfirmar}) {
    return (
        <Dialog open={aberto} onClose={ocupado ? undefined : onCancelar} maxWidth="sm" fullWidth>
            <DialogTitle>{titulo}</DialogTitle>
            <DialogContent><DialogContentText>{texto}</DialogContentText></DialogContent>
            <DialogActions>
                <Button onClick={onCancelar} disabled={ocupado}>Cancelar</Button>
                <Button variant="contained" color={perigo ? 'error' : 'primary'} onClick={onConfirmar} disabled={ocupado}>{ocupado ? 'Aguarde…' : rotulo}</Button>
            </DialogActions>
        </Dialog>
    );
}

/*
 * Página do plano numa versão (BBF-107, protótipo v2 "Planos → versão"): abas de versão, Comercial, Acesso,
 * publicar e descartar o rascunho, assinaturas da versão, perfis do plano e simulador. A versão publicada aparece
 * só para leitura; só o rascunho edita.
 */
export default function PlanoVersaoPage(props) {
    const {id, numero} = useParams();
    return <PaginaDaVersao key={`${id}:${numero || ''}`} {...props} />;
}

function PaginaDaVersao({openParent = true}) {
    const navigate = useNavigate();
    const {id, numero} = useParams();
    const dados = usePlanoVersao(id, numero ? Number(numero) : null);
    const {carregando, erro, plano, versoes, versao, modulos, bloqueios, licencas, recarregar, trocarVersao} = dados;

    const [form, setForm] = useState(null);
    const [avisoTrial, setAvisoTrial] = useState(0);
    const [ocupado, setOcupado] = useState('');
    const [confirmar, setConfirmar] = useState(null);
    const [feedback, setFeedback] = useState({error: '', success: ''});

    useEffect(() => {
        if (versao) setForm(formDaVersao(versao));
    }, [versao]);

    const editavel = versao?.situacao === 'RASCUNHO';
    const sujo = Boolean(editavel && form && rascunhoAlterado(form, versao));
    const rascunho = rascunhoDe(versoes);
    const publicadaAlguma = versoes.some((item) => item.situacao !== 'RASCUNHO');
    // A versão que o PUT do plano compara (ver `corpoDoPlano`): a base publicada, ou o rascunho num plano que nunca publicou.
    const versaoQueVale = versaoBase(versoes) || rascunho;
    const porVersao = useMemo(() => assinaturasPorVersao(licencas || []), [licencas]);

    const mostrarErro = useCallback((error, padrao) => setFeedback({error: getBackendErrorMessage(error, padrao), success: ''}), []);

    if (!numero && plano && versoes.length) {
        return <Navigate to={`/planos-comerciais/${plano.id}/versoes/${versaoPrincipal(versoes).numero}`} replace />;
    }

    const salvarRascunho = async () => {
        const response = await updateRascunho(plano.id, versao.numero, corpoDoRascunho(form));
        const atualizada = valorDe(response);
        if (atualizada) trocarVersao(atualizada);
        setAvisoTrial(0);
        return atualizada;
    };

    const acaoSalvarRascunho = async () => {
        setOcupado('salvar');
        try {
            await salvarRascunho();
            setFeedback({error: '', success: 'Rascunho salvo. Nada muda para quem assina até publicar.'});
        } catch (error) {
            mostrarErro(error, 'Não foi possível salvar o rascunho.');
        } finally {
            setOcupado('');
        }
    };

    // PUT do plano: só nome, descrição, vitrine ou perfis. Num plano que nunca publicou, o PUT copia o plano para o
    // rascunho e o publicaria se o plano fosse ATIVO: o rascunho é salvo antes e o plano fica INATIVO até publicar.
    const salvarPlano = async (mudancas) => {
        let base = versaoQueVale;
        if (!publicadaAlguma && editavel && sujo) base = await salvarRascunho();
        await updatePlano(plano.id, corpoDoPlano(plano, base, publicadaAlguma ? mudancas : {...mudancas, status: 'INATIVO'}));
        await recarregar();
    };

    const acaoSalvarPlano = async (mudancas) => {
        setOcupado('plano');
        try {
            await salvarPlano(mudancas);
            setFeedback({error: '', success: 'Plano salvo. Nenhuma versão mudou.'});
        } catch (error) {
            mostrarErro(error, 'Não foi possível salvar o plano.');
        } finally {
            setOcupado('');
        }
    };

    const acaoPublicar = async () => {
        setOcupado('publicar');
        try {
            if (sujo) await salvarRascunho();
            const response = await publicarVersao(plano.id, versao.numero);
            // A primeira publicação de um plano criado aqui o ativa: plano INATIVO não vai à vitrine nem ao autocadastro.
            if (!publicadaAlguma && plano.status !== 'ATIVO') {
                const publicada = valorDe(response)?.versao;
                await updatePlano(plano.id, corpoDoPlano(plano, publicada, {status: 'ATIVO'}));
            }
            setConfirmar(null);
            setFeedback({error: '', success: response?.data?.mensagem || `Versão ${versao.numero} publicada e à venda.`});
            await recarregar();
        } catch (error) {
            setConfirmar(null);
            const pendencias = error?.response?.data?.valor;
            if (pendencias?.erros) trocarVersao({numero: versao.numero, pendencias});
            mostrarErro(error, 'Não foi possível publicar a versão.');
        } finally {
            setOcupado('');
        }
    };

    const acaoDescartar = async () => {
        setOcupado('descartar');
        try {
            const response = await deleteRascunho(plano.id, versao.numero);
            setConfirmar(null);
            if (valorDe(response)?.plano_excluido) {
                navigate('/planos-comerciais', {replace: true});
                return;
            }
            const restantes = versoes.filter((item) => item.numero !== versao.numero);
            navigate(`/planos-comerciais/${plano.id}/versoes/${versaoPrincipal(restantes).numero}`, {replace: true});
        } catch (error) {
            setConfirmar(null);
            mostrarErro(error, 'Não foi possível descartar o rascunho.');
        } finally {
            setOcupado('');
        }
    };

    const acaoCriarRascunho = async () => {
        setOcupado('criar');
        try {
            const response = await createRascunho(plano.id, versao.numero);
            const novo = valorDe(response);
            navigate(`/planos-comerciais/${plano.id}/versoes/${novo?.numero || proximoNumero(versoes)}`);
        } catch (error) {
            mostrarErro(error, 'Não foi possível criar o rascunho.');
        } finally {
            setOcupado('');
        }
    };

    const mudarForm = (fn) => setForm((atual) => fn(atual));

    let conteudo;
    if (carregando && !plano) {
        conteudo = (
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{py: 6}}>
                <CircularProgress size={20} />
                <Typography color="text.secondary">Carregando o plano…</Typography>
            </Stack>
        );
    } else if (erro && !plano) {
        conteudo = <Alert severity="error">{erro}</Alert>;
    } else if (plano && !versao) {
        conteudo = <Alert severity="warning">Esta versão não existe no plano. <Link component={RouterLink} to={`/planos-comerciais/${plano.id}`}>Abrir a versão principal</Link>.</Alert>;
    } else if (plano && versao && form) {
        const nasAssinaturas = porVersao[versao.id] || [];
        conteudo = (
            <Stack spacing={3}>
                <Breadcrumbs>
                    <Link component={RouterLink} to="/planos-comerciais" underline="hover" color="inherit">Planos</Link>
                    <Typography color="text.primary">{plano.nome_plano}</Typography>
                </Breadcrumbs>

                <Stack direction={{xs: 'column', md: 'row'}} spacing={2} justifyContent="space-between" alignItems={{md: 'flex-start'}}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{minWidth: 0}}>
                        <SistemaLogo sistema={plano.sistema} />
                        <Box sx={{minWidth: 0}}>
                            <Typography variant="h4" component="h1" fontWeight={700} sx={{overflowWrap: 'anywhere'}}>
                                {plano.nome_plano} <Typography component="span" variant="h6" color="text.secondary">· {plano.sistema?.nome}</Typography>
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {plano.descricao ? `${plano.descricao} ` : ''}
                                {licencas ? (nasAssinaturas.length ? `${plural(nasAssinaturas.length, 'assinatura', 'assinaturas')} nesta versão.` : 'Nenhuma assinatura nesta versão.') : ''}
                            </Typography>
                        </Box>
                    </Stack>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{flexShrink: 0}}>
                        {editavel ? (
                            <>
                                <Button color="error" variant="outlined" disabled={Boolean(ocupado)} onClick={() => setConfirmar('descartar')}>Descartar</Button>
                                <Button variant="outlined" disabled={!sujo || Boolean(ocupado)} onClick={acaoSalvarRascunho}>{ocupado === 'salvar' ? 'Salvando…' : 'Salvar rascunho'}</Button>
                                <Button variant="contained" disabled={Boolean(ocupado)} onClick={() => setConfirmar('publicar')}>Publicar versão {versao.numero}</Button>
                            </>
                        ) : rascunho ? (
                            <Button variant="outlined" component={RouterLink} to={`/planos-comerciais/${plano.id}/versoes/${rascunho.numero}`}>Abrir rascunho (versão {rascunho.numero})</Button>
                        ) : (
                            <Button variant="contained" disabled={Boolean(ocupado)} onClick={acaoCriarRascunho}>
                                {ocupado === 'criar' ? 'Criando…' : `Criar versão ${proximoNumero(versoes)} a partir desta`}
                            </Button>
                        )}
                    </Stack>
                </Stack>

                <Paper variant="outlined" sx={{px: 1}}>
                    <Tabs value={versao.numero} variant="scrollable" scrollButtons="auto" aria-label="Versões do plano"
                          onChange={(_, valor) => navigate(`/planos-comerciais/${plano.id}/versoes/${valor}`)}>
                        {versoes.map((item) => (
                            <Tab key={item.numero} value={item.numero} sx={{textTransform: 'none'}}
                                 label={`Versão ${item.numero} · ${getSituacaoVersao(item.situacao).label}`} />
                        ))}
                    </Tabs>
                </Paper>

                <SituacaoDaVersao versao={versao} versoes={versoes} />
                {editavel ? <PendenciasDoRascunho pendencias={versao.pendencias} avisosLocais={avisosDoRascunho(form)} sujo={sujo} /> : null}

                <Box sx={{display: 'grid', gridTemplateColumns: {xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) minmax(0, 1.2fr)'}, gap: 3, alignItems: 'start'}}>
                    <ComercialCard
                        plano={plano}
                        versao={versao}
                        form={form}
                        editavel={editavel}
                        avisoTrial={avisoTrial}
                        salvandoPlano={ocupado === 'plano'}
                        onSalvarPlano={acaoSalvarPlano}
                        onMeio={(meio) => mudarForm((atual) => {
                            const {form: novo, trialZerado} = alternarMeio(atual, meio);
                            setAvisoTrial(trialZerado);
                            return novo;
                        })}
                        onCampo={(campo, valor) => mudarForm((atual) => ({...atual, [campo]: valor}))}
                    />
                    <AcessoCard
                        modulos={modulos}
                        form={form}
                        editavel={editavel}
                        bloqueios={bloqueios}
                        onSituacao={(idModulo, situacao) => mudarForm((atual) => definirSituacaoDoModulo(atual, idModulo, situacao))}
                        onPreco={(idModulo, preco) => mudarForm((atual) => definirPrecoDoOpcional(atual, idModulo, preco))}
                    />
                </Box>

                <SimuladorCard versao={versao} modulos={modulos} rascunhoSujo={sujo} />
                <AssinaturasDaVersao licencas={licencas === null ? null : nasAssinaturas} />
                <PerfisDoPlanoCard plano={plano} versaoQueVale={versaoQueVale} modulos={modulos} onSalvar={salvarPlano} />
            </Stack>
        );
    } else {
        conteudo = null;
    }

    return (
        <PlanosLayout openParent={openParent}>
            {conteudo}
            <Confirmacao
                aberto={confirmar === 'publicar'}
                titulo={versao ? `Publicar a versão ${versao.numero}?` : ''}
                texto={versao ? `${efeitoDePublicar(versao, versoes)} Versão publicada não muda.${sujo ? ' As mudanças não salvas são salvas antes.' : ''}` : ''}
                rotulo="Publicar"
                ocupado={ocupado === 'publicar'}
                onCancelar={() => setConfirmar(null)}
                onConfirmar={acaoPublicar}
            />
            <Confirmacao
                aberto={confirmar === 'descartar'}
                titulo={versao ? `Descartar o rascunho da versão ${versao.numero}?` : ''}
                texto={publicadaAlguma
                    ? 'O rascunho sai, e as outras versões continuam como estão.'
                    : 'O plano só tem este rascunho: descartar tira o plano da lista.'}
                rotulo="Descartar"
                perigo
                ocupado={ocupado === 'descartar'}
                onCancelar={() => setConfirmar(null)}
                onConfirmar={acaoDescartar}
            />
            <FeedbackSnackbar feedback={feedback} onClose={() => setFeedback({error: '', success: ''})} />
        </PlanosLayout>
    );
}
